import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { getApiUrl } from '../utils/apiUrl';
const hustroClientVersion = '2.91.0';

// Without a timeout a stalled request on a slow/flaky mobile connection
// hangs forever and the form never renders (PT-6956).
const REQUEST_TIMEOUT_MS = 30_000;

export const getHeaders = () => {
    return {
        'Content-Type': 'application/json',
        'hustro-client': 'simpleForm',
        'hustro-client-version': hustroClientVersion,
    };
};
export function getAuthHeader(token: string) {
    return { Authorization: `Bearer ${token}` };
}

const NETWORK_ERROR_CODES: readonly string[] = [
    AxiosError.ERR_NETWORK,
    AxiosError.ECONNABORTED,
    AxiosError.ETIMEDOUT,
];

export const isNetworkError = (error: unknown): boolean =>
    axios.isAxiosError(error) &&
    error.code != null &&
    NETWORK_ERROR_CODES.includes(error.code);

const MAX_RETRIES = 2;
const RETRY_DELAYS_MS = [1_000, 3_000];

interface RetriableRequestConfig extends AxiosRequestConfig {
    retryCount?: number;
}

// Retries GET requests that failed with a network error or timeout.
// Non-GET requests are not retried, as they may not be idempotent.
const attachNetworkErrorRetry = (client: AxiosInstance) => {
    client.interceptors.response.use(undefined, async (error: unknown) => {
        if (!axios.isAxiosError(error) || !error.config) {
            throw error;
        }

        const config = error.config as RetriableRequestConfig;
        const method = (config.method ?? 'get').toLowerCase();
        const retryCount = config.retryCount ?? 0;

        if (
            method !== 'get' ||
            !isNetworkError(error) ||
            retryCount >= MAX_RETRIES
        ) {
            throw error;
        }

        const retryDelayMs =
            RETRY_DELAYS_MS[retryCount] ??
            RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));

        config.retryCount = retryCount + 1;
        return client.request(config);
    });
};

const createApiClient = (baseURL: string) => {
    const client = axios.create({
        baseURL,
        headers: getHeaders(),
        timeout: REQUEST_TIMEOUT_MS,
    });
    attachNetworkErrorRetry(client);
    return client;
};

export const authorizedApiClient = createApiClient(`${getApiUrl()}/v2`);

export const unauthorizedApiClient = createApiClient(
    `${getApiUrl()}/unauthorized/`,
);

export const publicApiClient = createApiClient(`${getApiUrl()}/public`);
